// intake.service — orkestrasi Terima Servis (bangun tiket, aturan directWork).
// Mengikuti perilaku prototype v0.18 saveTerima().
import { genNo, genToken, todayISO, cleanText, wa62 } from '@domain/format';
import { currentUser } from '@core/session';
import { pushNotif } from '@core/notif';
import * as repo from './intake.repo';
import { activeSameUnit } from '@modules/queue/queue.repo';

export interface IntakeForm {
  outlet: string;
  no?: string; // no. servis (preview dari form; bila kosong dibuat di sini)
  custId: number | null;
  newCust?: { nama: string; kat: 'User' | 'Toko'; wa: string; telp?: string; lahir?: string; email?: string; kota?: string; alamat?: string } | null;
  jenis?: string; merk: string; model?: string; sn?: string; warna?: string;
  kelengkapan?: string; kondisi?: string; sandi?: string; keluhan: string;
  estBiaya?: number; estSelesai?: string; dp?: number; dpVia?: string; prioritas?: string;
  catatan?: string; teknisi?: string;
  dugaan?: number | null; caseKhusus?: boolean; caseKhususKet?: string;
  /** Foto kondisi fisik saat unit diterima (bukti bila dipersoalkan kemudian). */
  fotoUnit?: { path: string; fase: 'terima' | 'serah'; at: string; by: string }[];
  /** true = pengguna sudah diberi tahu ada tiket kembar dan tetap ingin membuat yang baru. */
  abaikanKembar?: boolean;
}

/** Dilempar saat unit yang sama masih punya tiket berjalan — view menanyakan dulu ke staf. */
export class TiketKembar extends Error {
  constructor(public readonly lain: { no: string; status: string; penerima: string | null }) {
    super(`Unit ini sudah punya tiket berjalan: ${lain.no} (${lain.status})`);
  }
}

export async function submitIntake(f: IntakeForm): Promise<string> {
  const u = currentUser();
  if (!u) throw new Error('Sesi tidak valid.');
  // I1: guard izin di lapis service (paritas v0.18 can('p_terima') = O/M/SPV/CS). RLS = backstop DB.
  if (!['Owner', 'Manager', 'SPV', 'CS'].includes(u.role)) throw new Error('Tidak punya izin menerima servis (hanya Owner/Manager/SPV/CS).');

  const merk = cleanText(f.merk, 100);
  const keluhan = cleanText(f.keluhan, 1000);
  if (!merk) throw new Error('Merk wajib diisi.');
  if (!keluhan) throw new Error('Keluhan wajib diisi.');

  let custId = f.custId;
  if (!custId && f.newCust?.nama) {
    const nm = cleanText(f.newCust.nama, 120);
    if (!nm) throw new Error('Nama pelanggan baru wajib diisi.');
    const wa = wa62(f.newCust.wa || f.newCust.telp || '');
    // WA wajib: itu satu-satunya penanda yang mencegah pelanggan yang sama dibuat berkali-kali.
    // Tanpa ini, tiap percobaan simpan yang gagal (internet putus) meninggalkan satu baris pelanggan baru.
    if (!wa) throw new Error('No. HP / WhatsApp pelanggan baru wajib diisi.');
    const dup = await repo.findCustomerByWa(wa);
    // Nomor sama = orang yang sama. JANGAN menolak: kalau tiket gagal disimpan setelah pelanggannya
    // terlanjur dibuat, percobaan berikutnya akan menabrak pelanggan buatannya sendiri dan
    // counter jadi buntu sampai halaman dimuat ulang. Cukup pakai id yang sudah ada.
    custId = dup ? dup.id : await repo.createCustomer({
      nama: nm, kat: f.newCust.kat, wa,
      telp: (f.newCust.telp ?? '').replace(/\D/g, ''),
      lahir: f.newCust.lahir, email: cleanText(f.newCust.email ?? '', 180),
      kota: cleanText(f.newCust.kota ?? '', 100), alamat: cleanText(f.newCust.alamat ?? '', 500),
      outlet: f.outlet,
    });
  }
  if (!custId) throw new Error('Pilih pelanggan atau isi pelanggan baru.');

  const est = f.estBiaya ?? 0;
  const tek = (f.teknisi ?? '').trim();
  const directWork = est > 0 && !!tek; // harga disetujui di counter → langsung dikerjakan
  const model = cleanText(f.model ?? '', 100);
  const unit = `${merk} ${model}`.trim();
  // Dua kasir menerima unit yang sama (pelanggan bawa 1 HP, A dan B sama-sama input) → tiket
  // kembar yang baru ketahuan saat serah terima. Cek sebelum menyimpan; staf tetap boleh
  // melanjutkan bila memang dua unit berbeda.
  if (!f.abaikanKembar) {
    const lain = await activeSameUnit({ customer_id: custId, sn: f.sn ?? '', merk, model }).catch(() => null);
    if (lain) throw new TiketKembar(lain);
  }

  let no = f.no?.trim() || genNo(f.outlet);
  // Form dibuka 23.58, tersimpan 00.05 → nomor bertanggal kemarin dan mengacaukan urutan harian.
  const hariIni = genNo(f.outlet).slice(0, f.outlet.length + 8);        // "OUT-yymmdd"
  if (!no.startsWith(hariIni)) no = genNo(f.outlet);

  const isi = (nomor: string): Parameters<typeof repo.createTicket>[0] => ({
      no: nomor, token: genToken(), customer_id: custId, outlet: f.outlet,
      status: directWork ? 'Dikerjakan' : 'Diterima',
      jenis: cleanText(f.jenis ?? 'HP', 80), merk, model,
      sn: cleanText(f.sn ?? '', 150), warna: cleanText(f.warna ?? '', 80), unit,
      kelengkapan: (f.kelengkapan ?? '').split(',').map((s) => s.trim()).filter(Boolean),
      kondisi: cleanText(f.kondisi ?? '', 1000), sandi: cleanText(f.sandi ?? '', 200), keluhan,
      est_biaya: est, est_selesai: f.estSelesai || null, lama: '', ket: '',
      dp: f.dp ?? 0, dp_via: (f.dp ?? 0) > 0 ? (f.dpVia || null) : null, prioritas: f.prioritas ?? 'P2', catatan: cleanText(f.catatan ?? '', 1000),
      penerima: u.nama, teknisi: tek, hold: false, kerja: directWork ? 'belum' : null,
      dugaan: f.dugaan ?? null, case_khusus: !!f.caseKhusus, case_khusus_ket: f.caseKhusus ? cleanText(f.caseKhususKet ?? '', 1000) : null,
      // Jalur foto sudah terunggah lebih dulu oleh form (bukan dari input pengguna langsung).
      foto_unit: (f.fotoUnit ?? []).slice(0, 8),
      tgl_iso: todayISO(),
  });

  // Nomor bentrok (dua kasir, detik yang sama) → ambil nomor baru, jangan tumpahkan error DB
  // ke layar. Cukup 3 kali: kalau masih bentrok, ada yang lain yang salah.
  for (let coba = 0; ; coba++) {
    try { await repo.createTicket(isi(no), u.nama); break; }
    catch (e) {
      if (!(e instanceof repo.NomorBentrok) || coba >= 2) throw e;
      no = genNo(f.outlet);
    }
  }
  if (directWork) {
    await repo.addLog(no, 'Dikerjakan', `Harga disetujui di counter (Rp ${est.toLocaleString('id-ID')}) — langsung dikerjakan oleh ${tek}`, u.nama);
  }

  // Dua kasir menekan Simpan pada detik yang sama: keduanya lolos pemeriksaan di atas karena
  // saat itu tiket lawannya memang belum ada. Periksa SEKALI LAGI setelah tersimpan — jangan
  // dibatalkan (unitnya sudah di tangan kita), cukup beri tahu supaya ketahuan hari itu juga,
  // bukan saat serah terima.
  if (!f.abaikanKembar) {
    const kembar = await activeSameUnit({ customer_id: custId, sn: f.sn ?? '', merk, model }, no).catch(() => null);
    if (kembar) {
      await repo.addLog(no, 'Diterima', `⚠️ Kemungkinan tiket kembar dengan ${kembar.no} (${kembar.status}) — unit sama. Periksa, gabungkan bila memang satu unit.`, u.nama).catch(() => undefined);
      try { await pushNotif('SPV', f.outlet, `⚠️ ${no} mungkin kembar dengan ${kembar.no} — unit sama diinput dua kali.`, no); } catch { /* best-effort */ }
    }
  }

  // Notif awal-rantai (best-effort): normal → SPV (tugaskan teknisi), directWork → teknisi (langsung kerjakan). Port saveTerima v0.18.
  try {
    // `tahap` = status tiket saat notif dibuat → lonceng otomatis MENGHILANGKAN notif ini begitu
    // tugasnya beres (tiket maju dari status itu), walau tak diklik. Tanpa tahap, notif "menunggu
    // penugasan" menumpuk selamanya meski teknisi sudah ditugaskan (bug yang dilaporkan).
    if (directWork) await pushNotif('TEK:' + tek, f.outlet, `🔧 ${no} (${unit}) langsung dikerjakan — harga disetujui di counter.`, no, 'Dikerjakan');
    else await pushNotif('SPV', f.outlet, `🆕 Tiket baru ${no} (${unit}) menunggu penugasan teknisi.`, no, 'Diterima');
  } catch { /* notif best-effort, tak menggagalkan intake */ }

  return no;
}
